import { Component, OnInit, OnDestroy } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { ActivatedRoute, Router, NavigationEnd, Event } from '@angular/router'
import { filter } from 'rxjs/operators'
import { Observable, Subscription } from 'rxjs'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { LoginService } from 'src/app/login/login.service'
import { AppSettingsService } from '../services/app-settings.service'
import { environment } from 'src/environments/environment'
import { VendorService } from 'src/app/multivendor/vendor.service'
import { DomainService } from 'src/app/domain/services/domain.service'

class ImageSnippet {
  constructor(public src: string, public file: File) {}
}

@Component({
  selector: 'app-basics',
  templateUrl: './app-basics.component.html',
  styleUrls: ['./app-basics.component.scss']
})
export class AppBasicsComponent implements OnInit, OnDestroy {
  selectedFile!: ImageSnippet
  private currentUrl: string = ''
  private prevUrl: string = ''
  private routerSub: Subscription | undefined
  isSampleEnabled: boolean = false
  enableAnimatedSplash: boolean = false
  project_id: string
  submittedData: any
  showProgress = false
  saving = false
  settingsForm = this.formBuilder.group({
    application_name: '',
    package_name: 'com.stacks.www',
    ipa_sku_number: '',
    application_description: 'Description',
    icon: '',
    splash_screen: '',
    animated_splash: '',
    animated_splash_duration: '',
    release_app: 'no',
    project_type: '',
    // Application Settings fields
    mobile_primary_color: '',
    mobile_secondary_color: '',
    mobile_text_color: '',
    button_text_color: '#ffffff'
  })

  fileToUpload: File | null = null

  generalSettingsData: any = {}
  applicationSettingsData: any = {}
  isFirstTimeSave = false
  constructor(private formBuilder: FormBuilder, private appSettingsService: AppSettingsService, public builderService: BuilderService, private router: Router, private route: ActivatedRoute, public loginService: LoginService, public vendor: VendorService, private domainService: DomainService) {
    this.builderService.showUpdateProgress = true
    // Wait for sometime till the project id is retrieved from the server if it is not available yet
    this.project_id = this.builderService.selectedProject
    if (!this.project_id) {
      setTimeout(() => {
        this.getInitialSettings()
      }, 1000)
    } else {
      this.getInitialSettings()
    }
  }
  /**
   * Initializes the settings form with data retrieved from the server.
   * @returns void
   *
   * Fetches the general settings for the specified project ID and updates the form with the retrieved values.
   * Sets default values for missing fields such as the application description and sample password. Ensures
   * the sample password is generated if not provided and updates the sample-enabled status. Hides the progress
   * indicator after the process is complete.
   */

  getInitialSettings = () => {
    // Get General Settings
    this.appSettingsService.getGeneralSettings(this.project_id).subscribe(
      (settings: any) => {
        this.generalSettingsData = settings || {}
        this.generalSettingsData.splash_screen = this.generalSettingsData.splash_screen ? this.generalSettingsData.splash_screen.replace('C:\\fakepath\\', environment.builderNativeURL + 'wp-content/uploads/project_assets/images/' + this.project_id + '/') : '';
        this.generalSettingsData.icon = this.generalSettingsData.icon ? this.generalSettingsData.icon.replace('C:\\fakepath\\', environment.builderNativeURL + 'wp-content/uploads/project_assets/images/' + this.project_id + '/') : '';
        this.generalSettingsData.animated_splash = this.generalSettingsData.animated_splash ? this.generalSettingsData.animated_splash.replace('C:\\fakepath\\', environment.builderNativeURL + 'wp-content/uploads/project_assets/images/' + this.project_id + '/') : '';
        if (settings) {
          this.settingsForm.patchValue({
            application_name: this.generalSettingsData.application_name || '',
            package_name: this.generalSettingsData.package_name || this.settingsForm.value.package_name,
            ipa_sku_number: this.generalSettingsData.ipa_sku_number || '',
            application_description: this.generalSettingsData.application_description || 'Description',
            release_app: this.generalSettingsData.release_app || 'no',
            project_type: this.generalSettingsData.project_type || '',
            animated_splash_duration: this.generalSettingsData.animated_splash_duration || '3000'
          })
        }
        this.isSampleEnabled = settings.isSampleEnabled ?? false
        this.enableAnimatedSplash = settings.enableAnimatedSplash ?? false

        this.isFirstTimeSave = !this.generalSettingsData.application_name
        this.builderService.showUpdateProgress = false
      },
      (err) => {
        console.log(err)
      }
    )

    // Get Application Settings (for colors)
    this.appSettingsService.getApplicationSettings(this.project_id).subscribe(
      (settings: any) => {
        console.log('application settings for colors: ',settings)
        this.applicationSettingsData = settings || {}
        if (settings) {
          this.settingsForm.patchValue({
             mobile_primary_color: settings.mobile_primary_color,
             mobile_secondary_color: settings.mobile_secondary_color,
             mobile_text_color: settings.mobile_text_color,
             button_text_color: settings.button_text_color || '#ffffff'
          })
        }
      },
      (err) => {
        console.log(err)
      }
    )
  }

  colorChange($event: any, field: any) {
    this.applicationSettingsData[field] = $event.target.value;
    this.settingsForm.get(field)?.setValue($event.target.value);
  }

  toggleSample(): void {
    this.isSampleEnabled = this.isSampleEnabled
  }

  toggleAnimatedSplash(): void {
    this.enableAnimatedSplash = this.enableAnimatedSplash
  }

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    this.routerSub = this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.prevUrl = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
      });  
  }
  
  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  /**
   * Handles the submission of general settings for the project.
   * @returns void
   *
   * Collects and processes data from the settings form, ensuring any unchanged fields retain their current values
   * from the existing general settings. Updates specific fields like icons, splash screens, and sample settings if
   * not provided in the form. Submits the finalized settings to the server and triggers the Auth0 redirect update.
   * Displays a progress indicator during the process and hides it upon completion.
   */

onSubmit(): void {
  // Process checkout data here
  this.saving = true
  let icon = this.settingsForm.value.icon
  let splash_screen = this.settingsForm.value.splash_screen
  let animated_splash = this.settingsForm.value.animated_splash
  
  let newSubmittedData: any = {}
  this.submittedData = this.settingsForm.value
  let retrievedData = this.generalSettingsData
  
  // Only include GENERAL settings fields, exclude color fields
  const generalSettingsFields = [
    'application_name',
    'package_name', 
    'ipa_sku_number',
    'application_description',
    'icon',
    'splash_screen',
    'animated_splash',
    'animated_splash_duration',
    'release_app',
    'project_type'
  ]
  
  // Fields backed by a <select> always carry a meaningful value (including ''),
  // so they must never fall back to the previously retrieved value when empty.
  const alwaysUseFormValueFields = ['project_type', 'release_app']

  for (var k in this.submittedData) {
    if (generalSettingsFields.includes(k)) {
      if (alwaysUseFormValueFields.includes(k) || this.submittedData[k] != '') {
        newSubmittedData[k] = this.submittedData[k]
      } else {
        newSubmittedData[k] = retrievedData[k]
      }
    }
  }
  
  if (!icon) {
    newSubmittedData['icon'] = this.generalSettingsData.icon
  }
  if (!splash_screen) {
    newSubmittedData['splash_screen'] = this.generalSettingsData.splash_screen
  }
  if (!animated_splash) {
    newSubmittedData['animated_splash'] = this.generalSettingsData.animated_splash;
  }
  if (newSubmittedData['animated_splash']) {
    newSubmittedData['animated_splash'] = newSubmittedData['animated_splash'].replace('C:\\fakepath\\', environment.builderNativeURL + 'wp-content/uploads/project_assets/images/' + this.builderService.selectedProject + '/')
  }
  if (retrievedData?.firebase_settings) {
    newSubmittedData['firebase_settings'] = this.generalSettingsData.firebase_settings
  }
  newSubmittedData['isSampleEnabled'] = this.isSampleEnabled
  newSubmittedData['enableAnimatedSplash'] = this.enableAnimatedSplash
  if (newSubmittedData['icon']) {
    newSubmittedData['icon'] = newSubmittedData['icon'].replace('C:\\fakepath\\', environment.builderNativeURL + 'wp-content/uploads/project_assets/images/' + this.builderService.selectedProject + '/')
  }
  if (newSubmittedData['splash_screen']) {
    newSubmittedData['splash_screen'] = newSubmittedData['splash_screen'].replace('C:\\fakepath\\', environment.builderNativeURL + 'wp-content/uploads/project_assets/images/' + this.builderService.selectedProject + '/')
  }

  this.appSettingsService.setGeneralSettings(this.builderService.selectedProject, newSubmittedData).subscribe(
    (response) => {
      // Always check and auto-generate subdomain if it doesn't exist
      if (newSubmittedData.application_name) {
        this.autoGenerateSubdomain(newSubmittedData.application_name)
      }
      
      // Update local color data first (prevents reverting) then save to backend
      this.saveApplicationSettings().subscribe(() => {
        // Update Auth Redirect (this now includes the colors)
        this.updateAuthRedirect()
      });
    },
    (err) => {
      console.warn(err)
      this.saving = false
    }
  )
}

saveApplicationSettings(): Observable<any> {
  // Get the current color values from the form
  const formValues = this.settingsForm.value;
  const colorSettings = {
    mobile_primary_color: formValues.mobile_primary_color,
    mobile_secondary_color: formValues.mobile_secondary_color,
    mobile_text_color: formValues.mobile_text_color,
    button_text_color: formValues.button_text_color
  };
  return this.appSettingsService.setApplicationSettings(this.project_id, colorSettings)
}

  /**
   * Auto-generate subdomain from application name on first save
   */
  autoGenerateSubdomain(appName: string): void {
    // Check if subdomain already exists
    this.domainService.getDomainSettings(this.builderService.selectedProject).subscribe(
      (domainSettings) => {
        // Only generate if no subdomain exists yet
        if (!domainSettings || !domainSettings.subdomain) {
          const generatedSubdomain = this.domainService.generateSubdomainFromName(appName)

          // Save the generated subdomain
          this.domainService.saveSubdomain(this.builderService.selectedProject, generatedSubdomain).subscribe(
            (response) => {
              console.log('Subdomain auto-generated:', response.subdomain_full)
            },
            (error) => {
              console.error('Failed to auto-generate subdomain:', error)
            }
          )
        }
      },
      (error) => {
        console.error('Failed to check domain settings:', error)
      }
    )
  }

  /**
   * Processes and uploads an image file for the specified type with validation.
   * @param imageInput The file input event containing the selected image.
   * @param type The type of the image being processed (e.g., 'icon', 'splash_screen').
   * @param minWidth The minimum required width for the image.
   * @param minHeight The minimum required height for the image.
   * @returns void
   *
   * Reads the selected image file, uploads it to the server, and validates its dimensions if the type requires it.
   * Updates the general settings data with the uploaded image URL upon successful upload. Displays a progress
   * indicator during the process and alerts the user if the image dimensions are smaller than the required minimum.
   * Handles errors gracefully and notifies the user if the upload fails.
   */

  processFile(imageInput: any, type: string, minWidth: number, minHeight: number) {
    if (type === 'animated_splash' && !this.enableAnimatedSplash) return;
    const file: File = imageInput.target.files[0]
    const reader = new FileReader()
    this.builderService.showUpdateProgress = true
    const isGif = file.type === 'image/gif'
    reader.addEventListener('load', (event: any) => {
      this.selectedFile = new ImageSnippet(event.target.result, file)
      this.appSettingsService.uploadImage(this.selectedFile.file, this.builderService.selectedProject, type).subscribe(
        (res) => {
          if (!res) {
            alert(res)
          } 
          else if (isGif && type === 'animated_splash') {
            this.generalSettingsData[type] = res; 
            this.calculateGifDuration(file).then((duration: number) => {
              this.settingsForm.patchValue({
                animated_splash_duration: String(duration)
              })
            })
          }
          else if (type == 'icon' || type == 'splash_screen') {
            const img = new Image()
            let $this = this
            img.src = res
            img.addEventListener('load', function () {
              if (this.width < minWidth || this.height < minHeight) {
                alert('Min image Dimensions = ' + minWidth + ' x ' + minHeight + ', Uploaded Dimensions = ' + this.width + ' x ' + this.height)
              } else {
                $this.generalSettingsData[type] = res
              }
            })
          } else {
            this.generalSettingsData[type] = res
          }

          this.builderService.showUpdateProgress = false
        },
        (err) => {
          this.builderService.showUpdateProgress = false
          alert('error uploading file, please contact support')
        }
      )
    })

    reader.readAsDataURL(file)
  }

  /**
   * Proceeds to the next step in the general settings workflow.
   * @returns void
   *
   * Submits the current general settings form and navigates to the next page based on the project's access type.
   * If the project access is 'webview', disables the tour and redirects to the content settings page. Otherwise,
   * redirects to the site type selection page.
   */

  generalSettingsNext = () => {
    this.onSubmit()
    if (this.builderService.projectAccess == 'webview') {
      this.builderService.setTour(false).subscribe(
        (response) => {
          this.router.navigate(['/contentsettings'], {
            relativeTo: this.route
          })
        },
        (err) => {}
      )
    } else {
      this.router.navigate(['/site-type'], { relativeTo: this.route })
    }
  }

  /**
   * Updates the Auth0 redirect URI in the application settings for a given project.
   * @param projectId The ID of the project whose settings are being updated.
   * @param submittedData The form data containing updates, including a new package name, if provided.
   * @returns void
   *
   * Retrieves the current application settings for the specified project, checks for a new package name in the submitted data,
   * and updates the Auth0 redirect URI by replacing the package name with the new one. Merges the updated redirect URI into the
   * current settings and saves the changes back to the application and WordPress settings.
   */

updateAuthRedirect() {
  this.appSettingsService.getApplicationSettings(this.project_id).subscribe(
    (currentSettings: any) => {
      const packageName = this.submittedData.package_name || this.settingsForm.value.package_name
      const auth0_domain = currentSettings.auth0_domain || 'dev-l0pw2od54vx31tmu.us.auth0.com'
      const auth0_redirect_uri = `${packageName}://${auth0_domain}/capacitor/${packageName}/callback`

      // IMPORTANT: Get the current color values from the form (the NEW values)
      const formValues = this.settingsForm.value;
      const colorSettings = {
        mobile_primary_color: formValues.mobile_primary_color,
        mobile_secondary_color: formValues.mobile_secondary_color,
        mobile_text_color: formValues.mobile_text_color,
        button_text_color: formValues.button_text_color
      };

      // Merge with colors to prevent overwriting them
      const updatedSettings = {
        ...currentSettings,
        ...colorSettings,  // Add the new color values
        auth0_redirect_uri: auth0_redirect_uri
      }

      this.appSettingsService.setApplicationSettings(this.project_id, updatedSettings).subscribe(() => {
        this.appSettingsService.setWPApplicationSettings(this.builderService.selectedProject, updatedSettings).subscribe(
          () => {
            this.saving = false
          },
          (error: any) => {
            console.error('Error syncing application settings', error)
            this.saving = false
          }
        )
      })
    },
    (error: any) => {
      console.error('Error saving social login data', error)
      this.saving = false
    }
  )
}
  /**
   * Reads the GIF file, scans its binary blocks, and sums all frame delays
   * to calculate the total animation duration in milliseconds.
   * Falls back to 3000ms if no delay metadata is found.
   */
  private calculateGifDuration(file: File): Promise<number> {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = (event: any) => {
        const buffer = new Uint8Array(event.target.result)
        let duration = 0
        for (let i = 0; i < buffer.length - 9; i++) {
          if (buffer[i] === 0x21 && buffer[i + 1] === 0xf9 && buffer[i + 2] === 0x04) {
            const delay = (buffer[i + 4] + buffer[i + 5] * 256) * 10 // convert to ms
            duration += delay
          }
        }
        resolve(duration || 3000)
      }
      reader.readAsArrayBuffer(file)
    })
  }
}
